import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios, { buildAuthenticatedUrl, createApiClient } from '../lib/apiClient';
import * as XLSX from "xlsx";
import {
  FiUserPlus,
  FiUsers,
  FiSearch,
  FiEdit2,
  FiEye,
  FiX,
  FiSave,
  FiRefreshCw,
  FiTrash2,
  FiDownload,
} from "react-icons/fi";
import { useUserId } from "../UserIdContext";
import { notifySuccess } from "../lib/notifications";
import {
  formatStringList,
  mergeDistinctStrings,
  normalizeStringList,
  resolveDepositoActual,
} from "./unidadesNegocioApi";
import "./cliente.css";

// API
const api = createApiClient({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

const API = {
  LISTAR: "/api/clientes/listar",
  CARGAR: "/api/clientes/cargar",
  SIGUIENTE_CODIGO: "/api/clientes/siguiente-codigo",
  EDITAR: (nombreCliente, id) => id
    ? `/api/clientes/editar/id/${encodeURIComponent(id)}`
    : `/api/clientes/editar/${encodeURIComponent(nombreCliente)}`,
  ELIMINAR: (id) => `/api/clientes/${id}`,
  DESCARGAR_PRESUPUESTO: (id) => `/api/clientes/${id}/presupuesto`,
  VER_PRESUPUESTO: (id) => `/api/clientes/${id}/presupuesto/ver`,
};

const PROVINCIAS = [
  "Buenos Aires",
  "Ciudad Autónoma de Buenos Aires",
  "Catamarca",
  "Chaco",
  "Chubut",
  "Córdoba",
  "Corrientes",
  "Entre Ríos",
  "Formosa",
  "Jujuy",
  "La Pampa",
  "La Rioja",
  "Mendoza",
  "Misiones",
  "Neuquén",
  "Río Negro",
  "Salta",
  "San Juan",
  "San Luis",
  "Santa Cruz",
  "Santa Fe",
  "Santiago del Estero",
  "Tierra del Fuego, Antártida e Islas del Atlántico Sur",
  "Tucumán",
];

const FILTER_OPTIONS = [
  "Ubicación",
  "Planta",
  "Unidad de negocio",
  "Lote",
  "Código",
  "Detalle de producto / N° FC",
  "Fecha ingreso",
  "Fecha elaboración",
  "Fecha vencimiento",
  "Tipo de embalaje",
  "Precinto SENASA",
  "Condición",
  "Observaciones",
  "Unidades por pallet",
  "Peso / Medidas",
];

const FILTER_OPTION_LABELS = {
  "Detalle de producto / N° FC": "Detalle de prod/Nro FC.",
};

const getFiltroDisplayLabel = (value) => FILTER_OPTION_LABELS[value] || value;

const emptyForm = {
  _id: "",
  codCliente: "",
  razonSocial: "",
  nombreFantasia: "",
  CUIT: "",
  direccion: "",
  codPostal: "",
  provincia: "",
  ciudad: "",
  condIva: "",
  contacto: "",
  telefono: "",
  email: "",
  bonificaciones: "",
  periodoFcInicio: "",
  periodoFcFin: "",
  procedencia: "",
  unidadNegocio: "",
  ubicacion: [],
  nombreCliente: "",
  observaciones: "",
  Filtros: [],
  codigosProducto: [],
  presupuesto: null,
};

const REMOVED_CLIENT_FIELDS = {
  cotizacion: "",
  tipoBusqueda: "",
  tipoUbicacion: "",
  tipoCliente: "",
  planta: "",
  pais: "",
  tipoCotizacion: "",
  zonaDeposito: "",
  tipoPago: [],
};

const CLIENT_FORM_KEYS = Object.keys(emptyForm);

const parseCodClienteNumber = (value) => {
  const digits = String(value ?? "")
    .trim()
    .replace(/[^\d.-]/g, "");
  if (!digits) return NaN;
  return Number(digits);
};

const compareByCodClienteAsc = (a, b) => {
  const codA = String(a?.codCliente ?? "").trim();
  const codB = String(b?.codCliente ?? "").trim();

  const numA = parseCodClienteNumber(codA);
  const numB = parseCodClienteNumber(codB);
  const hasNumA = Number.isFinite(numA);
  const hasNumB = Number.isFinite(numB);

  if (hasNumA && hasNumB && numA !== numB) return numA - numB;
  if (hasNumA !== hasNumB) return hasNumA ? -1 : 1;

  if (codA || codB) {
    const byCode = codA.localeCompare(codB, "es", {
      numeric: true,
      sensitivity: "base",
    });
    if (byCode !== 0) return byCode;
  }

  const nameA = String(a?.razonSocial || a?.nombreCliente || "");
  const nameB = String(b?.razonSocial || b?.nombreCliente || "");
  return nameA.localeCompare(nameB, "es", { sensitivity: "base" });
};

const formatCuitDisplay = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
  }

  return raw.replace(/\s+/g, "");
};

const normalizeSearchText = (value = "") =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const normalizeSearchDigits = (value = "") => String(value ?? "").replace(/\D/g, "");

const normalizeClienteCode = (value = "") =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const emptyCodigoProductoForm = {
  codigo: "",
  detalleProducto: "",
};

const normalizeClienteProductCodes = (value) => {
  if (!Array.isArray(value)) return [];

  const seen = new Set();

  return value.reduce((acc, item) => {
    if (!item || typeof item !== "object") return acc;

    const codigo = String(item?.codigo || item?.codProducto || "").trim();
    const detalleProducto = String(
      item?.detalleProducto || item?.detalle || item?.descripcion || ""
    ).trim();

    if (!codigo || !detalleProducto) return acc;

    const key = normalizeClienteCode(codigo);
    if (seen.has(key)) return acc;

    seen.add(key);
    acc.push({ codigo, detalleProducto });
    return acc;
  }, []);
};

const getClienteDisplayName = (cliente = {}) =>
  cliente?.razonSocial ||
  cliente?.nombreCliente ||
  cliente?.nombreFantasia ||
  cliente?.codCliente ||
  "otro cliente";

const matchesClienteSearch = (cliente, query) => {
  const textQuery = normalizeSearchText(query);
  const digitQuery = normalizeSearchDigits(query);
  if (!textQuery && !digitQuery) return true;

  const textFields = [
    cliente?.codCliente,
    cliente?.razonSocial,
    cliente?.nombreCliente,
  ].map(normalizeSearchText);

  if (textQuery && textFields.some((value) => value.includes(textQuery))) {
    return true;
  }

  if (digitQuery) {
    const cuitDigits = normalizeSearchDigits(cliente?.CUIT);
    if (cuitDigits.includes(digitQuery)) return true;
  }

  return false;
};

const normalizePostalCode = (value = "") => {
  const code = String(value).toUpperCase().replace(/\s+/g, "");
  return /^(?:\d{4}|[A-Z]\d{4}(?:[A-Z]{3})?)$/.test(code) ? code : "";
};

const normalizeDistinctStringArray = (value) => normalizeStringList(value);

const normalizePlantas = (value) => {
  const rawValues = Array.isArray(value)
    ? value
    : String(value ?? "").split(/[,;\n]+/);
  return mergeDistinctStrings(rawValues);
};

const resolveClientePlantas = (cliente = {}) =>
  mergeDistinctStrings(
    normalizePlantas(cliente?.plantas),
    normalizePlantas(cliente?.procedencia),
    normalizePlantas(cliente?.planta)
  );

const formatLocalDateStamp = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const sanitizeExcelText = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  return /^[=+\-@]/.test(text) ? `'${text}` : text;
};

const sanitizeExcelFilenamePart = (value, fallback = "clientes") => {
  const safe = String(value ?? "")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, " ")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return safe || fallback;
};

const normalizeClienteRecord = (cliente = {}) => ({
  ...cliente,
  plantas: resolveClientePlantas(cliente),
  unidadNegocio: normalizeDistinctStringArray(cliente?.unidadNegocio),
  ubicacion: normalizeDistinctStringArray(cliente?.ubicacion),
  Filtros: normalizeDistinctStringArray(cliente?.Filtros),
  codigosProducto: normalizeClienteProductCodes(cliente?.codigosProducto),
});

const pickClienteForm = (cliente = {}) =>
  CLIENT_FORM_KEYS.reduce((acc, key) => {
    const sourceValue = cliente?.[key] ?? emptyForm[key];
    if (key === "procedencia") {
      acc[key] = resolveClientePlantas(cliente).join(", ");
    } else if (key === "unidadNegocio") {
      acc[key] = normalizeDistinctStringArray(sourceValue).join(", ");
    } else if (key === "ubicacion" || key === "Filtros") {
      acc[key] = normalizeDistinctStringArray(sourceValue);
    } else if (key === "codigosProducto") {
      acc[key] = normalizeClienteProductCodes(sourceValue);
    } else {
      acc[key] = sourceValue;
    }
    return acc;
  }, {});

function useDebouncedValue(value, delay = 450) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

function ModalContainer({ children }) {
  const hostRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  if (!hostRef.current) {
    hostRef.current = document.createElement("div");
    hostRef.current.id = "modal-root";
  }

  useEffect(() => {
    document.body.appendChild(hostRef.current);
    setMounted(true);
    return () => {
      if (hostRef.current.parentNode) hostRef.current.parentNode.removeChild(hostRef.current);
    };
  }, []);

  return mounted ? createPortal(children, hostRef.current) : children;
}

function MultiValueField({
  label,
  values,
  onOpen,
  helperText,
}) {
  const selectedValues = normalizeDistinctStringArray(values);

  return (
    <div className="field multi-field">
      <label>{label}</label>
      <div className="multi-summary-row">
        <div className="multi-summary-box">
          <div className="chip-list">
            {selectedValues.length > 0 ? (
              selectedValues.map((value) => (
                <span key={value} className="chip chip-compact">
                  {value}
                </span>
              ))
            ) : (
              <span className="muted">Sin selecciones</span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="btn ghost multi-open-button"
          data-drawer-nav="true"
          onClick={onOpen}
        >
          Seleccionar
        </button>
      </div>
      {helperText ? <span className="muted">{helperText}</span> : null}
    </div>
  );
}

export default function GestorClientes({ clienteEditar = null, onClose, onSaved } = {}) {
  const { deposito } = useUserId();
  const depositoActual = resolveDepositoActual(deposito);
  const drawerFormRef = useRef(null);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(Boolean(clienteEditar));
  const [mode, setMode] = useState(clienteEditar ? "editar" : "crear");
  const [activeTab, setActiveTab] = useState("datos");
  const [form, setForm] = useState(() => clienteEditar ? pickClienteForm(clienteEditar) : emptyForm);
  const [editKey, setEditKey] = useState(
    clienteEditar ? clienteEditar.nombreCliente || clienteEditar.razonSocial || clienteEditar.codCliente : null
  );
  const [loadingNextCode, setLoadingNextCode] = useState(false);
  const [filtrosModalOpen, setFiltrosModalOpen] = useState(false);
  const [codigosModalOpen, setCodigosModalOpen] = useState(false);
  const [codigoProductoForm, setCodigoProductoForm] = useState(emptyCodigoProductoForm);
  const [codigoProductoEditIndex, setCodigoProductoEditIndex] = useState(null);
  const [codigoProductoError, setCodigoProductoError] = useState("");
  const [eliminando, setEliminando] = useState(false);
  const [presupuestoFile, setPresupuestoFile] = useState(null);
  const [previewPresupuestoOpen, setPreviewPresupuestoOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const presupuestoInputRef = useRef(null);
  const codigoProductoInputRef = useRef(null);
  const detalleProductoInputRef = useRef(null);

  const [page, setPage] = useState(1);
  const pageSize = 8;

  const [localidades, setLocalidades] = useState([]);
  const [loadingLoc, setLoadingLoc] = useState(false);
  const [errorLoc, setErrorLoc] = useState("");
  const [postalLookupLoading, setPostalLookupLoading] = useState(false);
  const [postalLookupMessage, setPostalLookupMessage] = useState("");
  const originalPostalCodeRef = useRef(
    clienteEditar?.provincia && clienteEditar?.ciudad
      ? normalizePostalCode(clienteEditar.codPostal)
      : ""
  );
  const [ubicacionesConfig, setUbicacionesConfig] = useState([]);
  const [multiSelectModal, setMultiSelectModal] = useState(null);
  const debouncedPostalCode = useDebouncedValue(form.codPostal);

  const fetchClientes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(API.LISTAR);
      setClientes(Array.isArray(data) ? data.map(normalizeClienteRecord) : []);
    } catch (e) {
      console.error("GET /clientes/listar:", e?.response || e);
      setClientes([]);
    } finally {
      setLoading(false);
    }
  };

  const crearCliente = (payload) => api.post(API.CARGAR, payload);
  const editarCliente = (payload) => api.put(API.EDITAR(editKey, form._id), payload);
  const eliminarCliente = (id) => api.delete(API.ELIMINAR(id));

  useEffect(() => { fetchClientes(); }, []);

  useEffect(() => {
    let active = true;

    api.get("/api/ocupaciones", {
      params: { deposito: depositoActual },
    })
      .then(({ data }) => {
        if (!active) return;
        const opciones = Array.isArray(data)
          ? data
              .filter((item) => item && item.visible !== false)
              .map((item) => item?.nombre)
              .filter(Boolean)
          : [];
        setUbicacionesConfig(opciones);
      })
      .catch((error) => {
        if (!active) return;
        console.error("GET /api/ocupaciones", error?.response || error);
        setUbicacionesConfig([]);
      });

    return () => {
      active = false;
    };
  }, [depositoActual]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setDrawerOpen(false);
      onClose?.();
    };
    if (drawerOpen) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen, onClose]);

  useEffect(() => {
    if (!drawerOpen) return undefined;

    const rafId = window.requestAnimationFrame(() => {
      const firstField =
        drawerFormRef.current?.querySelector('input[name="razonSocial"]') ||
        drawerFormRef.current?.querySelector('input[name="codCliente"]');
      firstField?.focus();
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [drawerOpen]);

  const filtrados = useMemo(() => {
    const k = q.trim();
    const base = !k
      ? clientes
      : clientes.filter((c) => matchesClienteSearch(c, k));
    return [...base].sort(compareByCodClienteAsc);
  }, [q, clientes]);

  const ubicacionOptions = useMemo(
    () => mergeDistinctStrings(ubicacionesConfig, form.ubicacion),
    [form.ubicacion, ubicacionesConfig]
  );

  const multiSelectConfig = useMemo(() => {
    if (multiSelectModal === "ubicacion") {
      return {
        field: "ubicacion",
        title: "Seleccionar ubicaciones",
        options: ubicacionOptions,
        helperText: "Podés asociar varias ubicaciones al mismo cliente.",
        emptyText: "No hay ubicaciones configuradas.",
      };
    }

    return null;
  }, [multiSelectModal, ubicacionOptions]);

  const codigosProducto = useMemo(
    () => normalizeClienteProductCodes(form.codigosProducto),
    [form.codigosProducto]
  );

  const totalPages = Math.max(1, Math.ceil(filtrados.length / pageSize));
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtrados.slice(start, start + pageSize);
  }, [filtrados, page]);

  const duplicatedCodCliente = useMemo(() => {
    const currentCode = normalizeClienteCode(form.codCliente);
    if (!currentCode) return null;

    return (
      clientes.find((cliente) => {
        if (!cliente) return false;
        if (form?._id && String(cliente._id) === String(form._id)) return false;
        return normalizeClienteCode(cliente.codCliente) === currentCode;
      }) || null
    );
  }, [clientes, form?._id, form.codCliente]);

  const fetchSuggestedCode = useCallback(async () => {
    try {
      setLoadingNextCode(true);
      const { data } = await api.get(API.SIGUIENTE_CODIGO);
      const suggestedCode = String(data?.data?.codCliente || "").trim();
      if (!suggestedCode) return "";

      setForm((prev) =>
        String(prev?.codCliente || "").trim()
          ? prev
          : { ...prev, codCliente: suggestedCode }
      );

      return suggestedCode;
    } catch (error) {
      console.error("GET /api/clientes/siguiente-codigo", error?.response || error);
      return "";
    } finally {
      setLoadingNextCode(false);
    }
  }, []);

  const codClienteError = duplicatedCodCliente
    ? `El código ya está asignado a ${getClienteDisplayName(duplicatedCodCliente)}.`
    : "";

  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  const openCrear = async () => {
    setMode("crear");
    setActiveTab("datos");
    setForm(emptyForm);
    setEditKey(null);
    setMultiSelectModal(null);
    setCodigosModalOpen(false);
    setCodigoProductoForm(emptyCodigoProductoForm);
    setCodigoProductoEditIndex(null);
    setCodigoProductoError("");
    setLocalidades([]);
    setPreviewPresupuestoOpen(false);
    setPostalLookupLoading(false);
    setPostalLookupMessage("");
    originalPostalCodeRef.current = "";
    setPresupuestoFile(null);
    if (presupuestoInputRef.current) presupuestoInputRef.current.value = "";
    setDrawerOpen(true);
    await fetchSuggestedCode();
  };

  const openEditar = async (c) => {
    setMode("editar");
    setActiveTab("datos");
    setLoadingNextCode(false);
    setEditKey(c.nombreCliente || c.razonSocial || c.codCliente);
    setMultiSelectModal(null);
    setCodigosModalOpen(false);
    setCodigoProductoForm(emptyCodigoProductoForm);
    setCodigoProductoEditIndex(null);
    setCodigoProductoError("");
    const next = {
      ...pickClienteForm(c),
      Filtros: Array.isArray(c?.Filtros) ? c.Filtros : [],
    };
    setForm(next);
    setPreviewPresupuestoOpen(false);
    setPostalLookupLoading(false);
    setPostalLookupMessage("");
    originalPostalCodeRef.current = next.provincia && next.ciudad
      ? normalizePostalCode(next.codPostal)
      : "";
    setPresupuestoFile(null);
    if (presupuestoInputRef.current) presupuestoInputRef.current.value = "";
    setDrawerOpen(true);
    if (next.provincia) fetchLocalidades(next.provincia);
    else setLocalidades([]);

    if (!String(next.codCliente || "").trim()) {
      await fetchSuggestedCode();
    }
  };

  const closeDrawer = () => {
    setLoadingNextCode(false);
    setMultiSelectModal(null);
    setCodigosModalOpen(false);
    setCodigoProductoForm(emptyCodigoProductoForm);
    setCodigoProductoEditIndex(null);
    setCodigoProductoError("");
    setPreviewPresupuestoOpen(false);
    setDrawerOpen(false);
    onClose?.();
  };

  const sanitizePayload = (data) => {
    const { _id, createdAt, updatedAt, __v, presupuesto, ...rest } = data || {};
    return rest;
  };

  const buildFormData = (payload, file) => {
    const fd = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || key === "presupuesto") return;
      if (Array.isArray(value)) {
        fd.append(key, JSON.stringify(value));
        return;
      }
      fd.append(key, String(value));
    });
    if (file) fd.append("presupuestoPdf", file);
    return fd;
  };

  const getPresupuestoUrl = (clienteId) => {
    const base = String(process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
    return buildAuthenticatedUrl(`${base}${API.DESCARGAR_PRESUPUESTO(clienteId)}`);
  };

  const getPresupuestoViewUrl = (clienteId) => {
    const base = String(process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
    return buildAuthenticatedUrl(`${base}${API.VER_PRESUPUESTO(clienteId)}`);
  };

  const openPresupuestoPicker = () => {
    presupuestoInputRef.current?.click();
  };

  const getDrawerNavigationTargets = () => {
    if (!drawerFormRef.current) return [];

    const selector = [
      "input:not([type='hidden']):not([type='file']):not([disabled]):not([readonly])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "button[data-drawer-nav='true']:not([disabled])",
    ].join(",");

    return Array.from(drawerFormRef.current.querySelectorAll(selector)).filter((element) => {
      if (element.classList.contains("file-input-hidden")) return false;
      if (element.tabIndex < 0) return false;
      if (element.getAttribute("aria-hidden") === "true") return false;

      const styles = window.getComputedStyle(element);
      if (styles.display === "none" || styles.visibility === "hidden") return false;

      return element.getClientRects().length > 0;
    });
  };

  const moveDrawerFocus = (currentElement, direction) => {
    const targets = getDrawerNavigationTargets();
    if (targets.length === 0) return false;

    const currentIndex = targets.findIndex((element) => element === currentElement);
    const fallbackIndex = direction > 0 ? 0 : targets.length - 1;
    const nextIndex = currentIndex >= 0 ? currentIndex + direction : fallbackIndex;
    const nextTarget =
      targets[nextIndex] ||
      (direction > 0
        ? drawerFormRef.current?.querySelector("button[type='submit']:not([disabled])")
        : null);

    if (!nextTarget) return false;

    nextTarget.focus();
    nextTarget.scrollIntoView({ block: "nearest", inline: "nearest" });
    return true;
  };

  const onDrawerFormKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (e.defaultPrevented || e.isComposing || e.ctrlKey || e.altKey || e.metaKey) return;

    const target = e.target;
    if (!(target instanceof HTMLElement)) return;

    const tagName = target.tagName;
    if (tagName === "TEXTAREA") return;
    if (tagName === "SELECT") return;
    if (tagName === "BUTTON" || tagName === "A") return;

    if (activeTab === "codigos" && !codigosModalOpen) {
      e.preventDefault();
      openCodigoProductoModal();
      return;
    }

    e.preventDefault();
    moveDrawerFocus(target, e.shiftKey ? -1 : 1);
  };

  const clearPresupuestoFile = () => {
    setPresupuestoFile(null);
    if (presupuestoInputRef.current) {
      presupuestoInputRef.current.value = "";
    }
  };

  const onPresupuestoFileChange = (e) => {
    const nextFile = e.target.files?.[0] || null;
    setPresupuestoFile(nextFile);
  };

  const toggleFiltro = (nombre) => {
    setForm((f) => {
      const next = new Set(f.Filtros || []);
      next.has(nombre) ? next.delete(nombre) : next.add(nombre);
      return { ...f, Filtros: Array.from(next) };
    });
  };

  const toggleMultiValue = (field, value) => {
    setForm((prev) => {
      const next = new Set(normalizeDistinctStringArray(prev?.[field]));
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...prev, [field]: Array.from(next) };
    });
  };

  const selectAllFiltros = () => {
    setForm((f) => ({ ...f, Filtros: [...FILTER_OPTIONS] }));
  };

  const closeCodigosModal = () => {
    setCodigosModalOpen(false);
    setCodigoProductoForm(emptyCodigoProductoForm);
    setCodigoProductoEditIndex(null);
    setCodigoProductoError("");
  };

  const openCodigoProductoModal = (index = null) => {
    const nextItem =
      index !== null && codigosProducto[index]
        ? codigosProducto[index]
        : emptyCodigoProductoForm;

    setCodigoProductoForm({
      codigo: String(nextItem?.codigo || ""),
      detalleProducto: String(nextItem?.detalleProducto || ""),
    });
    setCodigoProductoEditIndex(index);
    setCodigoProductoError("");
    setCodigosModalOpen(true);
  };

  useEffect(() => {
    if (!codigosModalOpen) return undefined;

    const rafId = window.requestAnimationFrame(() => {
      codigoProductoInputRef.current?.focus();
      codigoProductoInputRef.current?.select?.();
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [codigosModalOpen]);

  const saveCodigoProducto = () => {
    const codigo = String(codigoProductoForm?.codigo || "").trim();
    const detalleProducto = String(codigoProductoForm?.detalleProducto || "").trim();

    if (!codigo || !detalleProducto) {
      setCodigoProductoError("Completa codigo y detalle para guardarlo.");
      return;
    }

    const codigoKey = normalizeClienteCode(codigo);
    const duplicatedCodeIndex = codigosProducto.findIndex(
      (item, index) =>
        index !== codigoProductoEditIndex &&
        normalizeClienteCode(item?.codigo) === codigoKey
    );

    if (duplicatedCodeIndex >= 0) {
      setCodigoProductoError(`El codigo ${codigo} ya existe en este cliente.`);
      return;
    }

    setForm((prev) => {
      const nextCodigos = [...normalizeClienteProductCodes(prev?.codigosProducto)];
      const nextItem = { codigo, detalleProducto };

      if (codigoProductoEditIndex !== null && nextCodigos[codigoProductoEditIndex]) {
        nextCodigos[codigoProductoEditIndex] = nextItem;
      } else {
        nextCodigos.push(nextItem);
      }

      return {
        ...prev,
        codigosProducto: nextCodigos,
      };
    });

    closeCodigosModal();
  };

  const handleCodigoProductoModalKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (e.defaultPrevented || e.isComposing || e.ctrlKey || e.altKey || e.metaKey) return;

    const target = e.target;
    if (!(target instanceof HTMLElement)) return;

    const tagName = target.tagName;
    if (tagName === "TEXTAREA" || tagName === "BUTTON" || tagName === "A") return;

    e.preventDefault();

    const detalleProducto = String(codigoProductoForm?.detalleProducto || "").trim();
    if (target === codigoProductoInputRef.current && !detalleProducto) {
      detalleProductoInputRef.current?.focus();
      return;
    }

    saveCodigoProducto();
  };

  const deleteCodigoProducto = (index) => {
    setForm((prev) => ({
      ...prev,
      codigosProducto: normalizeClienteProductCodes(prev?.codigosProducto).filter(
        (_item, itemIndex) => itemIndex !== index
      ),
    }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === "codCliente") return;
    setForm((f) => ({ ...f, [name]: value }));
    if (name === "provincia") {
      setForm((f) => ({ ...f, provincia: value, ciudad: "" }));
      if (value) fetchLocalidades(value);
      else setLocalidades([]);
    }
  };

  const validar = () => {
    if (loadingNextCode) {
      return "Espera a que se asigne el codigo automatico.";
    }

    const req = ["razonSocial", "codCliente"];
    for (const k of req) {
      if (!String(form[k] || "").trim()) return `Falta completar: ${k}`;
    }
    if (duplicatedCodCliente) {
      return `El código ${String(form.codCliente || "").trim()} ya está en uso.`;
    }
    if (form.CUIT && !/^\d{11}$/.test(String(form.CUIT))) return "CUIT debe tener 11 dígitos (sin guiones).";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validar();
    if (err) return alert(err);

    const plantas = normalizePlantas(form.procedencia);
    const plantaPrincipal = plantas[0] || "";
    const razonSocial = String(form.razonSocial || "").trim();
    const payload = {
      ...form,
      ...REMOVED_CLIENT_FIELDS,
      razonSocial,
      nombreCliente:
        razonSocial ||
        form.nombreCliente ||
        form.nombreFantasia ||
        form.codCliente,
      ubicacion: normalizeDistinctStringArray(form.ubicacion),
      unidadNegocio: normalizePlantas(form.unidadNegocio),
      plantas,
      procedencia: plantaPrincipal,
      planta: plantaPrincipal,
      Filtros: Array.from(new Set(form.Filtros || [])),
      codigosProducto: normalizeClienteProductCodes(form.codigosProducto),
    };
    const cleanPayload = sanitizePayload(payload);
    const formData = buildFormData(cleanPayload, presupuestoFile);

    try {
      setLoading(true);
      const { data: clienteGuardado } = mode === "crear"
        ? await crearCliente(formData)
        : await editarCliente(formData);
      if (!clienteEditar) {
        notifySuccess(mode === "crear" ? "Cliente creado correctamente." : "Cliente actualizado correctamente.");
      }
      onSaved?.(clienteGuardado);
      closeDrawer();
      setPresupuestoFile(null);
      if (presupuestoInputRef.current) presupuestoInputRef.current.value = "";
      if (!clienteEditar) await fetchClientes();
    } catch (ex) {
      console.error("Guardar cliente:", ex?.response || ex);
      alert(ex?.response?.data?.error || ex?.response?.data?.mensaje || "No pude guardar el cliente. Intentá nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const onDeleteCliente = async (c) => {
    if (!c?._id) return;
    if (!window.confirm(`Eliminar cliente ${c.razonSocial || c.nombreCliente || c.codCliente}?`)) return;
    try {
      setEliminando(true);
      await eliminarCliente(c._id);
      notifySuccess("Cliente eliminado correctamente.");
      await fetchClientes();
    } catch (err) {
      console.error("DELETE cliente", err?.response || err);
      alert("No se pudo eliminar el cliente");
    } finally {
      setEliminando(false);
    }
  };

  const exportarClientes = async () => {
    if (filtrados.length === 0) return;

    try {
      setExporting(true);

      const headers = [
        "Codigo",
        "Razon social",
        "Nombre fantasia",
        "CUIT",
        "Cond. IVA",
        "Contacto",
        "Email",
        "Telefono",
        "Direccion",
        "Codigo postal",
        "Ciudad",
        "Provincia",
        "Unidad negocio",
        "Ubicacion",
        "Procedencia / Plantas",
        "Bonificaciones",
        "Periodo FC inicio",
        "Periodo FC fin",
        "Filtros",
        "Observaciones",
      ];

      const aoa = [
        headers,
        ...filtrados.map((cliente) => [
          sanitizeExcelText(cliente?.codCliente),
          sanitizeExcelText(cliente?.razonSocial || cliente?.nombreCliente),
          sanitizeExcelText(cliente?.nombreFantasia),
          sanitizeExcelText(formatCuitDisplay(cliente?.CUIT)),
          sanitizeExcelText(cliente?.condIva),
          sanitizeExcelText(cliente?.contacto),
          sanitizeExcelText(cliente?.email),
          sanitizeExcelText(cliente?.telefono),
          sanitizeExcelText(cliente?.direccion),
          sanitizeExcelText(cliente?.codPostal),
          sanitizeExcelText(cliente?.ciudad),
          sanitizeExcelText(cliente?.provincia),
          sanitizeExcelText(formatStringList(cliente?.unidadNegocio, "")),
          sanitizeExcelText(formatStringList(cliente?.ubicacion, "")),
          sanitizeExcelText(formatStringList(resolveClientePlantas(cliente), "")),
          sanitizeExcelText(cliente?.bonificaciones),
          sanitizeExcelText(cliente?.periodoFcInicio),
          sanitizeExcelText(cliente?.periodoFcFin),
          sanitizeExcelText(formatStringList(cliente?.Filtros, "")),
          sanitizeExcelText(cliente?.observaciones),
        ]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = [
        { wch: 10 },
        { wch: 34 },
        { wch: 28 },
        { wch: 16 },
        { wch: 16 },
        { wch: 24 },
        { wch: 34 },
        { wch: 18 },
        { wch: 32 },
        { wch: 14 },
        { wch: 22 },
        { wch: 22 },
        { wch: 26 },
        { wch: 26 },
        { wch: 22 },
        { wch: 18 },
        { wch: 16 },
        { wch: 16 },
        { wch: 30 },
        { wch: 40 },
      ];
      ws["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: aoa.length - 1, c: headers.length - 1 },
        }),
      };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Clientes");

      const scope = q.trim()
        ? sanitizeExcelFilenamePart(q, "filtro")
        : `todos_${filtrados.length}`;
      XLSX.writeFile(wb, `Clientes_${scope}_${formatLocalDateStamp()}.xlsx`);
      notifySuccess("Archivo de clientes generado correctamente.");
    } catch (error) {
      console.error("Exportar clientes", error);
      alert("No pude exportar los clientes. Revisa la consola para mas detalles.");
    } finally {
      setExporting(false);
    }
  };

  const fetchLocalidades = useCallback(async (provinciaNombre) => {
    setLoadingLoc(true);
    setErrorLoc("");
    setLocalidades([]);
    try {
      const { data } = await axios.get("https://apis.datos.gob.ar/georef/api/localidades", {
        params: { provincia: provinciaNombre, campos: "id,nombre", max: 5000 },
        timeout: 15000,
      });
      const list = (data?.localidades || [])
        .map((l) => ({ id: l.id, nombre: l.nombre }))
        .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
      setLocalidades(list);
    } catch (e) {
      console.error("Error localidades:", e?.message || e);
      setErrorLoc("No pude cargar localidades");
    } finally {
      setLoadingLoc(false);
    }
  }, []);

  useEffect(() => {
    if (!clienteEditar) return;
    if (clienteEditar.provincia) fetchLocalidades(clienteEditar.provincia);
    if (!String(clienteEditar.codCliente || "").trim()) fetchSuggestedCode();
  }, [clienteEditar, fetchLocalidades, fetchSuggestedCode]);

  useEffect(() => {
    if (!drawerOpen) return undefined;

    const cp = normalizePostalCode(debouncedPostalCode);
    if (!cp || (mode === "editar" && cp === originalPostalCodeRef.current)) {
      setPostalLookupLoading(false);
      setPostalLookupMessage("");
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        setPostalLookupLoading(true);
        setPostalLookupMessage("");

        const { data } = await api.get("/api/geo/codigo-postal", { params: { cp } });
        if (cancelled) return;

        const provincia = String(data?.provincia || "").trim();
        const ciudad = String(data?.ciudad || "").trim();

        if (provincia) {
          fetchLocalidades(provincia);
        }

        setForm((prev) => {
          if (normalizePostalCode(prev.codPostal) !== cp) return prev;
          return {
            ...prev,
            provincia: provincia || prev.provincia,
            ciudad: ciudad || prev.ciudad,
          };
        });

        setPostalLookupMessage(
          provincia || ciudad
            ? `Autocompletado: ${[ciudad, provincia].filter(Boolean).join(" / ")}`
            : ""
        );
      } catch (error) {
        if (cancelled) return;
        const status = error?.response?.status;
        setPostalLookupMessage(
          status === 404
            ? "El autocompletado no encontró este código postal. Podés ingresar provincia y ciudad manualmente."
            : "El autocompletado no está disponible. Podés ingresar provincia y ciudad manualmente."
        );
      } finally {
        if (!cancelled) setPostalLookupLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedPostalCode, drawerOpen, fetchLocalidades, mode]);

  return (
    <div className={clienteEditar ? "cliente-editor-embedded" : "clients-wrap"}>
      {!clienteEditar && (
      <>
      <div className="clients-header">
        <div className="title-stack">
          <h1>
            <FiUsers /> Gestor de Clientes
          </h1>
          <p className="subtitle">Alta, edición y consulta de clientes</p>
          <p className="clientes-count">Clientes: {clientes.length}</p>
        </div>

        <div className="toolbar">
          <div className="input-search">
            <FiSearch />
            <input
              placeholder="Buscar por código, razón social o CUIT..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <button type="button" className="btn ghost" onClick={fetchClientes} title="Refrescar">
            <FiRefreshCw /> Refrescar
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={exportarClientes}
            disabled={exporting || filtrados.length === 0}
            title="Exportar clientes"
          >
            <FiDownload /> {exporting ? "Exportando..." : "Exportar Excel"}
          </button>
          <button type="button" className="btn solid" onClick={openCrear}>
            <FiUserPlus /> Agregar Clientes
          </button>
        </div>
      </div>

      <div className="panel table-panel">
        <div className="table-responsive">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Código</th>
                <th>Razón social</th>
                <th>Nombre fantasía</th>
                <th className="cuit-col">CUIT</th>
                <th>Cond. IVA</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th>Unidad negocio</th>
                <th>Ciudad / Prov.</th>
                <th style={{ width: 140 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {!loading && pageData.length === 0 && (
                <tr>
                  <td colSpan={10} className="empty">Sin resultados</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={10} className="empty">Cargando...</td>
                </tr>
              )}
              {pageData.map((c) => (
                <tr key={c._id || c.codCliente || c.nombreCliente}>
                  <td><span className="mono">{c.codCliente || "—"}</span></td>
                  <td>{c.razonSocial || c.nombreCliente || "—"}</td>
                  <td>{c.nombreFantasia || "—"}</td>
                  <td className="cuit-col cuit-cell">
                    <span className="mono">{formatCuitDisplay(c.CUIT) || "—"}</span>
                  </td>
                  <td>{c.condIva || "—"}</td>
                  <td>{c.email || "—"}</td>
                  <td>{c.telefono || "—"}</td>
                  <td>{formatStringList(c.unidadNegocio)}</td>
                  <td>{[c.ciudad, c.provincia].filter(Boolean).join(" / ") || "—"}</td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button type="button" className="btn icon" onClick={() => openEditar(c)} title="Editar">
                        <FiEdit2 />
                      </button>
                      <button
                        type="button"
                        className="btn icon"
                        title="Eliminar"
                        disabled={eliminando}
                        onClick={() => onDeleteCliente(c)}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pager">
          <button type="button" className="btn ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            Anterior
          </button>
          <span className="pager-info">Página {page} / {totalPages}</span>
          <button type="button" className="btn ghost" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            Siguiente
          </button>
        </div>
      </div>

      </>
      )}

      {drawerOpen && (
        <ModalContainer>
          <div className="drawer-viewport">
            <div className="drawer-overlay show" onClick={closeDrawer} aria-hidden />
            <aside className={`drawer cliente-modal ${drawerOpen ? "open" : ""} ${mode === "crear" ? "create" : ""}`} role="dialog" aria-modal="true" aria-label={mode === "crear" ? "Agregar cliente" : "Editar cliente"} onClick={(e) => e.stopPropagation()}>
              <div className="drawer-header">
                <h3>{mode === "crear" ? "Agregar cliente" : "Editar cliente"}</h3>
                <button type="button" className="btn icon ghost" onClick={closeDrawer}><FiX /></button>
              </div>

              <form
                ref={drawerFormRef}
                className="drawer-body"
                onSubmit={onSubmit}
                onKeyDown={onDrawerFormKeyDown}
              >
                <div className="drawer-tabs" role="tablist" aria-label="Secciones del cliente">
                  <button
                    type="button"
                    className={`drawer-tab ${activeTab === "datos" ? "active" : ""}`}
                    onClick={() => setActiveTab("datos")}
                  >
                    Datos
                  </button>
                  <button
                    type="button"
                    className={`drawer-tab ${activeTab === "codigos" ? "active" : ""}`}
                    onClick={() => setActiveTab("codigos")}
                  >
                    Codigos
                    <span className="drawer-tab-count">{codigosProducto.length}</span>
                  </button>
                </div>

                {activeTab === "datos" && (
                <div className="form-grid drawer-grid">
                <div className="form-section-label">Datos del cliente</div>
                <div className="field is-readonly">
                  <label>Código *</label>
                  <input type="hidden" name="codCliente" value={form.codCliente} />
                  <div
                    className={`static-field-value mono ${loadingNextCode ? "is-loading" : ""} ${codClienteError ? "is-invalid" : ""}`}
                    aria-live="polite"
                  >
                    {loadingNextCode ? "Asignando codigo..." : form.codCliente || "Sin asignar"}
                  </div>
                  {!codClienteError && loadingNextCode ? (
                    <span className="muted">Buscando el siguiente codigo disponible...</span>
                  ) : !codClienteError ? (
                    <span className="muted field-readonly-note">
                      Se asigna automaticamente y no se puede editar.
                    </span>
                  ) : null}
                  {codClienteError ? <span className="field-error">{codClienteError}</span> : null}
                </div>
                <div className="field grid-span-2">
                  <label>Razón social *</label>
                  <input name="razonSocial" value={form.razonSocial} onChange={onChange} />
                </div>

                <div className="field grid-span-2">
                  <label>Nombre de fantasía</label>
                  <input name="nombreFantasia" value={form.nombreFantasia} onChange={onChange} />
                </div>
                <div className="field">
                  <label>CUIT</label>
                  <input name="CUIT" value={form.CUIT} onChange={onChange} placeholder="Solo números, sin guiones" aria-describedby="cliente-cuit-ayuda" />
                  <span id="cliente-cuit-ayuda" className="muted">
                    Ingresá los 11 dígitos del CUIT sin guiones.
                  </span>
                </div>

                <div className="field grid-span-2">
                  <label>Dirección</label>
                  <input name="direccion" value={form.direccion} onChange={onChange} />
                </div>
                <div className="field">
                  <label>Código postal</label>
                  <input
                    name="codPostal"
                    value={form.codPostal}
                    onChange={onChange}
                    placeholder="Ej: 2000"
                  />
                  {postalLookupLoading && (
                    <span className="muted">Buscando provincia y ciudad...</span>
                  )}
                  {!postalLookupLoading && postalLookupMessage && (
                    <span className="muted">{postalLookupMessage}</span>
                  )}
                </div>

                <div className="field">
                  <label>Provincia</label>
                  <select name="provincia" value={form.provincia} onChange={onChange}>
                    <option value="">Seleccione</option>
                    {PROVINCIAS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                    {form.provincia && !PROVINCIAS.includes(form.provincia) && (
                      <option value={form.provincia}>{form.provincia}</option>
                    )}
                  </select>
                </div>

                <div className="field grid-span-2">
                  <label>Ciudad / Localidad</label>
                  <select
                    name="ciudad"
                    value={form.ciudad}
                    onChange={onChange}
                    disabled={!form.provincia}
                  >
                    {!form.provincia ? (
                      <option value="">Seleccione provincia primero</option>
                    ) : (
                      <>
                        <option value="">
                          {loadingLoc
                            ? "Cargando localidades..."
                            : errorLoc
                              ? errorLoc
                              : "Seleccione"}
                        </option>
                        {form.ciudad && !localidades.some((l) => l.nombre === form.ciudad) && (
                          <option value={form.ciudad}>{form.ciudad}</option>
                        )}
                        {localidades.map((l) => (
                          <option key={l.id} value={l.nombre}>{l.nombre}</option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                <div className="field">
                  <label>Condición IVA</label>
                  <input name="condIva" value={form.condIva} onChange={onChange} />
                </div>
                <div className="field grid-span-2">
                  <label>Contacto</label>
                  <input name="contacto" value={form.contacto} onChange={onChange} />
                </div>

                <div className="field">
                  <label>Teléfono</label>
                  <input name="telefono" value={form.telefono} onChange={onChange} />
                </div>
                <div className="field grid-span-2">
                  <label>Email</label>
                  <input name="email" type="text" inputMode="email" value={form.email} onChange={onChange} />
                </div>
                <div className="field">
                  <label>Bonificaciones</label>
                  <input name="bonificaciones" value={form.bonificaciones} onChange={onChange} />
                </div>

                <div className="field">
                  <label>Periodo FC inicio</label>
                  <input name="periodoFcInicio" value={form.periodoFcInicio} onChange={onChange} />
                </div>
                <div className="field">
                  <label>Periodo FC fin</label>
                  <input name="periodoFcFin" value={form.periodoFcFin} onChange={onChange} />
                </div>
                <div className="field grid-span-3">
                  <label>Procedencia / Planta(s)</label>
                  <input
                    name="procedencia"
                    value={form.procedencia}
                    onChange={onChange}
                    placeholder="Ej: Planta Norte, Planta Sur"
                  />
                  <span className="muted">
                    Puede cargar varias plantas separadas por coma.
                  </span>
                </div>

                <div className="field grid-span-2">
                  <label>Cotización (PDF)</label>
                  <input
                    ref={presupuestoInputRef}
                    className="file-input-hidden"
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={onPresupuestoFileChange}
                  />
                  <div className="file-picker-row">
                    <button type="button" className="btn ghost" onClick={openPresupuestoPicker}>
                      {presupuestoFile ? "Cambiar PDF" : "Seleccionar PDF"}
                    </button>
                    {presupuestoFile && (
                      <button type="button" className="btn ghost" onClick={clearPresupuestoFile}>
                        Quitar PDF
                      </button>
                    )}
                    <span className={`file-picker-name ${presupuestoFile ? "is-selected" : ""}`}>
                      {presupuestoFile ? presupuestoFile.name : "Ningún archivo seleccionado"}
                    </span>
                  </div>
                  <p className="muted" style={{ margin: "4px 0 0" }}>
                    {presupuestoFile
                      ? "PDF listo para guardar."
                      : "Seleccioná un PDF para adjuntar la cotización del cliente."}
                  </p>
                  {mode === "editar" && form?._id && form?.presupuesto?.relativePath && (
                    <div className="file-actions-row">
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={() => setPreviewPresupuestoOpen(true)}
                      >
                        <FiEye /> Ver PDF
                      </button>
                      <a
                        className="btn ghost file-current-link"
                        href={getPresupuestoUrl(form._id)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <FiDownload /> Descargar
                      </a>
                    </div>
                  )}
                </div>

                <div className="field grid-span-2">
                  <label htmlFor="cliente-unidad-negocio">Unidad de negocio</label>
                  <input
                    id="cliente-unidad-negocio"
                    name="unidadNegocio"
                    value={form.unidadNegocio}
                    onChange={onChange}
                    placeholder="Ej: Alimentos, Comercio Exterior"
                  />
                  <span className="muted">
                    Escribí las unidades separadas por coma. Para quitar una, borrá su nombre.
                    Se guardan solo para este cliente al guardar el formulario.
                  </span>
                </div>

                <div className="grid-span-2">
                  <MultiValueField
                    label="Ubicaciones"
                    values={form.ubicacion}
                    onOpen={() => setMultiSelectModal("ubicacion")}
                    helperText="Podés asociar varias ubicaciones al mismo cliente."
                  />
                </div>

                <div className="field grid-span-full">
                  <label>Observaciones</label>
                  <input name="observaciones" value={form.observaciones} onChange={onChange} />
                </div>

                <div className="field grid-span-2">
                  <label>Filtros a mostrar en el egreso</label>
                  <div className="filter-row">
                    <div className="chip-list">
                      {(form.Filtros || []).length === 0 && <span className="muted">Sin filtros seleccionados</span>}
                      {(form.Filtros || []).map((f) => (
                        <span key={f} className="chip chip-compact">{getFiltroDisplayLabel(f)}</span>
                      ))}
                    </div>
                    <button type="button" className="btn ghost" onClick={() => setFiltrosModalOpen(true)}>
                      Seleccionar filtros
                    </button>
                  </div>
                  <p className="muted" style={{ margin: "4px 0 0" }}>
                    Estos filtros se mostrarán luego en el panel de egreso para este cliente.
                  </p>
                </div>

              </div>
                )}

                {activeTab === "codigos" && (
                  <div className="codes-panel">
                    <div className="codes-summary">
                      <div>
                        <h4>Codigos y detalle de producto</h4>
                        <p className="muted">
                          Al usar este cliente en Ingreso, escribir el codigo completara el detalle automaticamente.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn solid"
                        onClick={() => openCodigoProductoModal()}
                      >
                        Agregar codigo
                      </button>
                    </div>

                    <div className="codes-overview">
                      <span className="chip chip-compact">
                        {codigosProducto.length} {codigosProducto.length === 1 ? "codigo cargado" : "codigos cargados"}
                      </span>
                      <span className="muted">
                        Si el codigo no existe, en Ingreso se podra completar codigo y detalle manualmente.
                      </span>
                    </div>

                    {codigosProducto.length === 0 ? (
                      <div className="empty codes-empty">
                        Todavia no hay codigos guardados para este cliente.
                      </div>
                    ) : (
                      <div className="codes-table-wrap">
                        <table className="codes-table">
                          <thead>
                            <tr>
                              <th>Codigo</th>
                              <th>Detalle de producto</th>
                              <th style={{ width: 120 }}>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {codigosProducto.map((item, index) => (
                              <tr key={`${item.codigo}-${index}`}>
                                <td><span className="mono">{item.codigo}</span></td>
                                <td>{item.detalleProducto}</td>
                                <td>
                                  <div className="codes-actions">
                                    <button
                                      type="button"
                                      className="btn icon"
                                      title="Editar codigo"
                                      onClick={() => openCodigoProductoModal(index)}
                                    >
                                      <FiEdit2 />
                                    </button>
                                    <button
                                      type="button"
                                      className="btn icon"
                                      title="Eliminar codigo"
                                      onClick={() => deleteCodigoProducto(index)}
                                    >
                                      <FiTrash2 />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

              <div className="drawer-footer">
                <button type="button" className="btn ghost" onClick={closeDrawer}>
                  <FiX /> Cancelar
                </button>
                <button
                  type="submit"
                  className="btn solid"
                  disabled={
                    Boolean(codClienteError) ||
                    loadingNextCode ||
                    !String(form.codCliente || "").trim()
                  }
                >
                  <FiSave /> Guardar
                </button>
              </div>
              </form>
            </aside>
          </div>
        </ModalContainer>
      )}

      {filtrosModalOpen && (
        <ModalContainer>
          <div className="mini-modal-overlay" onClick={() => setFiltrosModalOpen(false)} aria-hidden>
            <div className="mini-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="mini-modal-header">
                <div>
                  <h4>Filtros para el panel de egreso</h4>
                </div>
                <button type="button" className="btn ghost" onClick={() => setFiltrosModalOpen(false)}><FiX /> Cerrar</button>
              </div>

              <div className="filter-grid">
                {FILTER_OPTIONS.map((opt) => {
                  const checked = (form.Filtros || []).includes(opt);
                  return (
                    <label key={opt} className={`filter-option ${checked ? "on" : ""}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleFiltro(opt)}
                      />
                      <span>{getFiltroDisplayLabel(opt)}</span>
                    </label>
                  );
                })}
              </div>

              <div className="mini-modal-actions">
                <button type="button" className="btn ghost" onClick={selectAllFiltros}>
                  Seleccionar todos
                </button>
                <button type="button" className="btn ghost" onClick={() => setForm((f) => ({ ...f, Filtros: [] }))}>
                  Limpiar filtros
                </button>
                <button type="button" className="btn solid" onClick={() => setFiltrosModalOpen(false)}>
                  Guardar selección
                </button>
              </div>
            </div>
          </div>
        </ModalContainer>
      )}

      {multiSelectConfig && (
        <ModalContainer>
          <div className="mini-modal-overlay" onClick={() => setMultiSelectModal(null)} aria-hidden>
            <div className="mini-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="mini-modal-header">
                <div>
                  <h4>{multiSelectConfig.title}</h4>
                  <p className="muted" style={{ margin: "4px 0 0" }}>
                    {multiSelectConfig.helperText}
                  </p>
                </div>
                <button type="button" className="btn ghost" onClick={() => setMultiSelectModal(null)}>
                  <FiX /> Cerrar
                </button>
              </div>

              {multiSelectConfig.options.length > 0 ? (
                <div className="filter-grid">
                  {multiSelectConfig.options.map((option) => {
                    const checked = normalizeDistinctStringArray(form?.[multiSelectConfig.field]).includes(option);
                    return (
                      <label key={option} className={`filter-option ${checked ? "on" : ""}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleMultiValue(multiSelectConfig.field, option)}
                        />
                        <span>{option}</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="empty">{multiSelectConfig.emptyText}</div>
              )}

              <div className="mini-modal-actions">
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => setForm((prev) => ({ ...prev, [multiSelectConfig.field]: [] }))}
                >
                  Limpiar selección
                </button>
                <button type="button" className="btn solid" onClick={() => setMultiSelectModal(null)}>
                  Guardar selección
                </button>
              </div>
            </div>
          </div>
        </ModalContainer>
      )}

      {codigosModalOpen && (
        <ModalContainer>
          <div className="mini-modal-overlay" onClick={closeCodigosModal} aria-hidden>
            <div
              className="mini-modal mini-modal-codes"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleCodigoProductoModalKeyDown}
              role="dialog"
              aria-modal="true"
            >
              <div className="mini-modal-header">
                <div>
                  <h4>{codigoProductoEditIndex !== null ? "Editar codigo" : "Agregar codigo"}</h4>
                  <p className="muted" style={{ margin: "4px 0 0" }}>
                    Guarda el codigo y el detalle para recuperarlos automaticamente en Ingreso.
                  </p>
                </div>
                <button type="button" className="btn ghost" onClick={closeCodigosModal}>
                  <FiX /> Cerrar
                </button>
              </div>

              <div className="codes-modal-grid">
                <div className="field">
                  <label>Codigo</label>
                  <input
                    ref={codigoProductoInputRef}
                    value={codigoProductoForm.codigo}
                    onChange={(e) => {
                      setCodigoProductoForm((prev) => ({ ...prev, codigo: e.target.value }));
                      if (codigoProductoError) setCodigoProductoError("");
                    }}
                    placeholder="Ej: ABC123"
                  />
                </div>

                <div className="field">
                  <label>Detalle de producto</label>
                  <input
                    ref={detalleProductoInputRef}
                    value={codigoProductoForm.detalleProducto}
                    onChange={(e) => {
                      setCodigoProductoForm((prev) => ({
                        ...prev,
                        detalleProducto: e.target.value,
                      }));
                      if (codigoProductoError) setCodigoProductoError("");
                    }}
                    placeholder="Detalle de producto"
                  />
                </div>
              </div>

              {codigoProductoError ? (
                <div className="field-error">{codigoProductoError}</div>
              ) : null}

              <div className="mini-modal-actions">
                <button type="button" className="btn ghost" onClick={closeCodigosModal}>
                  Cancelar
                </button>
                <button type="button" className="btn solid" onClick={saveCodigoProducto}>
                  Guardar codigo
                </button>
              </div>
            </div>
          </div>
        </ModalContainer>
      )}

      {previewPresupuestoOpen && form?._id && (
        <ModalContainer>
          <div className="pdf-modal-overlay" onClick={() => setPreviewPresupuestoOpen(false)} aria-hidden>
            <div
              className="pdf-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Vista previa de cotización"
            >
              <div className="pdf-modal-header">
                <h4>Cotización guardada</h4>
                <div className="pdf-modal-actions">
                  <a
                    className="btn ghost file-current-link"
                    href={getPresupuestoUrl(form._id)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FiDownload /> Descargar
                  </a>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => setPreviewPresupuestoOpen(false)}
                  >
                    <FiX /> Cerrar
                  </button>
                </div>
              </div>
              <iframe
                className="pdf-modal-frame"
                src={getPresupuestoViewUrl(form._id)}
                title="Vista previa PDF"
              />
            </div>
          </div>
        </ModalContainer>
      )}
    </div>
  );
}
